import React, { useEffect, useRef, useState } from 'react';
import { useNavigate, useLocation } from 'react-router-dom';
import { loadStripe } from '@stripe/stripe-js';
import Header from '../Header';
import Header2 from '../Header2';
import Footer from '../Footer';
import { useAuth } from '../../auth/AuthContext';
import {
  createGiftCardIntent,
  confirmGiftCardCheckout,
  redeemGiftCard,
  fetchMyGiftCards,
} from '../../api/starviewApi';
import './GiftCards.css';

const DESKTOP_BREAKPOINT = 900;
const AMOUNT_OPTIONS = [2500, 5000, 10000, 15000];
const DESIGNS = [
  { id: 'classic', label: 'Classic', swatch: 'linear-gradient(135deg, #001830, #8A2BE2)' },
  { id: 'birthday', label: 'Birthday', swatch: 'linear-gradient(135deg, #FF6EC7, #8A2BE2)' },
  { id: 'holiday', label: 'Holiday', swatch: 'linear-gradient(135deg, #1E7A3E, #87CEEB)' },
  { id: 'thank-you', label: 'Thank You', swatch: 'linear-gradient(135deg, #E5A93C, #8A2BE2)' },
];

export default function GiftCards() {
  const navigate = useNavigate();
  const location = useLocation();
  const { isAuthenticated, authFetch } = useAuth();
  const [isDesktop, setIsDesktop] = useState(false);

  useEffect(() => {
    const check = () => setIsDesktop(window.innerWidth >= DESKTOP_BREAKPOINT);
    check();
    window.addEventListener('resize', check);
    return () => window.removeEventListener('resize', check);
  }, []);

  // --- Purchase form state ---
  const [amountCents, setAmountCents] = useState(5000);
  const [customAmount, setCustomAmount] = useState('');
  const [recipientMode, setRecipientMode] = useState('self'); // 'self' | 'someone-else'
  const [recipientName, setRecipientName] = useState('');
  const [recipientEmail, setRecipientEmail] = useState('');
  const [senderName, setSenderName] = useState('');
  const [message, setMessage] = useState('');
  const [design, setDesign] = useState('classic');

  const [step, setStep] = useState('form'); // 'form' | 'payment' | 'success'
  const [clientSecret, setClientSecret] = useState(null);
  const [purchasedCard, setPurchasedCard] = useState(null);
  const [paying, setPaying] = useState(false);
  const [payError, setPayError] = useState('');
  const stripeRef = useRef(null);
  const cardElementRef = useRef(null);
  const cardMountRef = useRef(null);

  // --- Redeem state ---
  const [redeemCode, setRedeemCode] = useState('');
  const [redeeming, setRedeeming] = useState(false);
  const [redeemMessage, setRedeemMessage] = useState('');
  const [redeemError, setRedeemError] = useState('');

  // --- My gift cards ---
  const [myCards, setMyCards] = useState([]);
  const [loadingMine, setLoadingMine] = useState(false);

  const loadMyCards = async () => {
    if (!isAuthenticated) return;
    setLoadingMine(true);
    try {
      const cards = await fetchMyGiftCards(authFetch);
      setMyCards(cards);
    } catch (err) {
      // best-effort, non-blocking
    } finally {
      setLoadingMine(false);
    }
  };

  useEffect(() => {
    loadMyCards();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [isAuthenticated]);

  const finalAmountCents = customAmount ? Math.round(parseFloat(customAmount) * 100) || 0 : amountCents;

  const requireAuth = () => {
    if (!isAuthenticated) {
      navigate('/Login', { state: { from: `${location.pathname}${location.search}` } });
      return true;
    }
    return false;
  };

  const handleStartPayment = async () => {
    if (requireAuth()) return;
    if (finalAmountCents < 1000 || finalAmountCents > 50000) {
      setPayError('Choose an amount between $10 and $500.');
      return;
    }
    if (recipientMode === 'someone-else' && !recipientEmail.trim()) {
      setPayError("Enter the recipient's email so they can redeem the code.");
      return;
    }
    setPayError('');
    setPaying(true);
    try {
      const intent = await createGiftCardIntent(authFetch, { amountCents: finalAmountCents });
      setClientSecret(intent.clientSecret);
      stripeRef.current = await loadStripe(intent.publishableKey);
      setStep('payment');
    } catch (err) {
      setPayError(err.message || 'Failed to start checkout.');
    } finally {
      setPaying(false);
    }
  };

  useEffect(() => {
    if (step !== 'payment' || !stripeRef.current || !cardMountRef.current) return;
    const elements = stripeRef.current.elements();
    const card = elements.create('card', {
      style: { base: { color: '#fff', fontSize: '16px', '::placeholder': { color: 'rgba(255,255,255,0.4)' } } },
    });
    card.mount(cardMountRef.current);
    cardElementRef.current = card;
    return () => card.unmount();
  }, [step]);

  const handlePay = async () => {
    if (!stripeRef.current || !cardElementRef.current) return;
    setPayError('');
    setPaying(true);
    try {
      const result = await stripeRef.current.confirmCardPayment(clientSecret, {
        payment_method: { card: cardElementRef.current },
      });
      if (result.error) throw new Error(result.error.message || 'Payment failed.');
      if (result.paymentIntent.status !== 'succeeded') {
        throw new Error(`Payment not completed (status: ${result.paymentIntent.status})`);
      }
      const confirmation = await confirmGiftCardCheckout(authFetch, {
        paymentIntentId: result.paymentIntent.id,
        amountCents: finalAmountCents,
        recipientName: recipientMode === 'someone-else' ? recipientName.trim() : undefined,
        recipientEmail: recipientMode === 'someone-else' ? recipientEmail.trim() : undefined,
        senderName: senderName.trim() || undefined,
        message: message.trim() || undefined,
        design,
      });
      setPurchasedCard(confirmation.giftCard);
      setStep('success');
      loadMyCards();
    } catch (err) {
      setPayError(err.message || 'Payment failed.');
    } finally {
      setPaying(false);
    }
  };

  const handleRedeem = async (e) => {
    e.preventDefault();
    if (requireAuth()) return;
    if (!redeemCode.trim()) return;
    setRedeeming(true);
    setRedeemError('');
    setRedeemMessage('');
    try {
      const result = await redeemGiftCard(authFetch, { code: redeemCode.trim() });
      setRedeemMessage(
        result.alreadyRedeemed
          ? `This code is already in your account — balance $${(result.giftCard.balance_cents / 100).toFixed(2)}.`
          : `Added to your account! Balance: $${(result.giftCard.balance_cents / 100).toFixed(2)}.`
      );
      setRedeemCode('');
      loadMyCards();
    } catch (err) {
      setRedeemError(err.message || 'Failed to redeem gift card.');
    } finally {
      setRedeeming(false);
    }
  };

  const resetForm = () => {
    setStep('form');
    setClientSecret(null);
    setPurchasedCard(null);
    setPayError('');
    setAmountCents(5000);
    setCustomAmount('');
    setRecipientMode('self');
    setRecipientName('');
    setRecipientEmail('');
    setSenderName('');
    setMessage('');
    setDesign('classic');
  };

  const mailtoHref = purchasedCard && purchasedCard.recipient_email
    ? `mailto:${encodeURIComponent(purchasedCard.recipient_email)}?subject=${encodeURIComponent('A StarView Cinema Gift Card for you!')}&body=${encodeURIComponent(
        `Hi ${purchasedCard.recipient_name || ''},\n\n${purchasedCard.sender_name || 'Someone'} sent you a $${(purchasedCard.initial_amount_cents / 100).toFixed(2)} StarView Cinema gift card!\n\nRedeem it at starviewcinema.com/GiftCards with code: ${purchasedCard.code}\n\n${purchasedCard.message || ''}`
      )}`
    : null;

  return (
    <div className="giftcards-root">
      <Header />
      {isDesktop && <Header2 />}

      <section className="gc-hero">
        <div className="gc-hero-inner">
          <span className="gc-eyebrow">StarView Gift Cards</span>
          <h1 className="gc-title">Give the Gift of the Big Screen</h1>
          <p className="gc-subtitle">Redeemable on tickets across every StarView Cinema location. Buy it for yourself or send it to someone else — either way, it's real money on a real account.</p>
        </div>
      </section>

      <section className="gc-purchase-section">
        <div className="gc-purchase-card">
          {step === 'form' && (
            <>
              <h2 className="gc-section-title">Choose an amount</h2>
              <div className="gc-amount-grid">
                {AMOUNT_OPTIONS.map((amt) => (
                  <button
                    key={amt}
                    className={`gc-amount-btn${!customAmount && amountCents === amt ? ' active' : ''}`}
                    onClick={() => { setAmountCents(amt); setCustomAmount(''); }}
                  >
                    ${(amt / 100).toFixed(0)}
                  </button>
                ))}
                <input
                  className={`gc-amount-custom${customAmount ? ' active' : ''}`}
                  placeholder="Custom $"
                  inputMode="decimal"
                  value={customAmount}
                  onChange={(e) => setCustomAmount(e.target.value.replace(/[^0-9.]/g, ''))}
                />
              </div>

              <h2 className="gc-section-title">Who is it for?</h2>
              <div className="gc-toggle-row">
                <button className={`gc-toggle-btn${recipientMode === 'self' ? ' active' : ''}`} onClick={() => setRecipientMode('self')}>For Myself</button>
                <button className={`gc-toggle-btn${recipientMode === 'someone-else' ? ' active' : ''}`} onClick={() => setRecipientMode('someone-else')}>For Someone Else</button>
              </div>

              {recipientMode === 'someone-else' && (
                <div className="gc-fields">
                  <input className="gc-input" placeholder="Recipient name" value={recipientName} onChange={(e) => setRecipientName(e.target.value)} />
                  <input className="gc-input" placeholder="Recipient email" type="email" value={recipientEmail} onChange={(e) => setRecipientEmail(e.target.value)} />
                  <input className="gc-input" placeholder="Your name" value={senderName} onChange={(e) => setSenderName(e.target.value)} />
                  <textarea className="gc-input gc-textarea" placeholder="Add a personal message (optional)" value={message} onChange={(e) => setMessage(e.target.value)} />
                </div>
              )}

              <h2 className="gc-section-title">Pick a design</h2>
              <div className="gc-design-grid">
                {DESIGNS.map((d) => (
                  <button
                    key={d.id}
                    className={`gc-design-swatch${design === d.id ? ' active' : ''}`}
                    style={{ background: d.swatch }}
                    onClick={() => setDesign(d.id)}
                  >
                    {d.label}
                  </button>
                ))}
              </div>

              {!!payError && <p className="gc-error">{payError}</p>}

              <button className="gc-cta-btn" onClick={handleStartPayment} disabled={paying}>
                {paying ? 'Please wait…' : `Continue — $${(finalAmountCents / 100).toFixed(2)}`}
              </button>
            </>
          )}

          {step === 'payment' && (
            <>
              <h2 className="gc-section-title">Payment</h2>
              <p className="gc-payment-summary">${(finalAmountCents / 100).toFixed(2)} gift card{recipientMode === 'someone-else' && recipientName ? ` for ${recipientName}` : ''}</p>
              <div ref={cardMountRef} className="gc-card-element" />
              {!!payError && <p className="gc-error">{payError}</p>}
              <button className="gc-cta-btn" onClick={handlePay} disabled={paying}>
                {paying ? 'Processing…' : `Pay $${(finalAmountCents / 100).toFixed(2)}`}
              </button>
              <button className="gc-back-btn" onClick={() => setStep('form')} disabled={paying}>Back</button>
            </>
          )}

          {step === 'success' && purchasedCard && (
            <div className="gc-success">
              <div className="gc-success-icon">✔</div>
              <h2 className="gc-section-title">Gift Card Purchased!</h2>
              <div className="gc-code-display" style={{ background: DESIGNS.find((d) => d.id === purchasedCard.design)?.swatch }}>
                <span className="gc-code-amount">${(purchasedCard.initial_amount_cents / 100).toFixed(2)}</span>
                <span className="gc-code-value">{purchasedCard.code}</span>
              </div>
              {purchasedCard.recipient_email ? (
                <>
                  <p className="gc-success-note">This code isn't tied to your account yet — {purchasedCard.recipient_name || 'they'} redeem it themselves at StarView Cinema once you send it along.</p>
                  <a className="gc-cta-btn gc-cta-secondary" href={mailtoHref}>Send via Email</a>
                </>
              ) : (
                <p className="gc-success-note">Added straight to your account — spend it on any ticket at checkout.</p>
              )}
              <button className="gc-back-btn" onClick={resetForm}>Buy another</button>
            </div>
          )}
        </div>
      </section>

      <section className="gc-redeem-section">
        <div className="gc-redeem-card">
          <h2 className="gc-section-title">Redeem a Gift Card</h2>
          <p className="gc-redeem-desc">Got a code from someone? Add it to your account to spend at checkout.</p>
          <form className="gc-redeem-form" onSubmit={handleRedeem}>
            <input
              className="gc-input"
              placeholder="SVC-XXXX-XXXX"
              value={redeemCode}
              onChange={(e) => setRedeemCode(e.target.value.toUpperCase())}
            />
            <button className="gc-cta-btn gc-redeem-btn" type="submit" disabled={redeeming}>
              {redeeming ? 'Redeeming…' : 'Redeem'}
            </button>
          </form>
          {!!redeemMessage && <p className="gc-redeem-success">{redeemMessage}</p>}
          {!!redeemError && <p className="gc-error">{redeemError}</p>}
        </div>
      </section>

      {isAuthenticated && (
        <section className="gc-mine-section">
          <h2 className="gc-section-title gc-mine-title">My Gift Cards</h2>
          {loadingMine ? (
            <p className="gc-mine-empty">Loading…</p>
          ) : myCards.length === 0 ? (
            <p className="gc-mine-empty">No gift cards on your account yet.</p>
          ) : (
            <div className="gc-mine-grid">
              {myCards.map((card) => (
                <div key={card.gift_card_id} className="gc-mine-item" style={{ background: DESIGNS.find((d) => d.id === card.design)?.swatch }}>
                  <span className="gc-mine-balance">${(card.balance_cents / 100).toFixed(2)}</span>
                  <span className="gc-mine-code">{card.code}</span>
                  <span className="gc-mine-status">{card.status === 'depleted' ? 'Used' : 'Active'}</span>
                </div>
              ))}
            </div>
          )}
        </section>
      )}

      <Footer />
    </div>
  );
}
